import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Rss, Star, Users, Sparkles } from "lucide-react";
import { getFeed } from "../lib/api";
import { useAuth } from "../lib/auth";

// ---------------------------------------------------------------------------
// Feed — chronological activity from the handles you follow. Signed-in only.
// Two empty states: (a) not signed in, (b) signed in but not following anyone.
// ---------------------------------------------------------------------------

export default function Feed() {
  const { user, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    getFeed(50)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((err) => { if (!cancelled) setError(err?.response?.data?.detail || "Could not load feed"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user, authLoading]);

  if (authLoading || loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#FF3B30]" />
      </div>
    );
  }

  if (!user) {
    return (
      <FeedShell>
        <EmptyState
          title="Sign in to follow other viewers"
          body="Follow @handles from any public profile to see a live feed of what they're watching, dropping, and finishing."
          testid="feed-signed-out"
        />
      </FeedShell>
    );
  }

  if (error) {
    return (
      <FeedShell>
        <EmptyState title="Couldn't load feed" body={error} testid="feed-error" />
      </FeedShell>
    );
  }

  const items = data?.items || [];
  return (
    <FeedShell count={items.length}>
      {items.length === 0 ? (
        <EmptyState
          title={data?.empty_reason === "no_public_follows" ? "No public profiles yet" : "Your feed is empty"}
          body={
            data?.empty_reason === "no_public_follows"
              ? "The handles you follow haven't made their profiles public yet."
              : "Head to any /u/{handle} page and hit Follow to start seeing their activity here."
          }
          testid="feed-empty"
        />
      ) : (
        <ul data-testid="feed-list" className="mt-6 divide-y divide-white/10 border-y border-white/10">
          {items.map((it, i) => (
            <FeedItem key={`${it.user.handle}-${it.anime.mal_id}-${i}`} item={it} />
          ))}
        </ul>
      )}
    </FeedShell>
  );
}

// ---------------------------------------------------------------------------

function FeedShell({ children, count }) {
  return (
    <div className="mx-auto max-w-[900px] px-6 py-10 md:px-10">
      <div className="mb-8 flex items-end justify-between border-b border-white/10 pb-4">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-[#FF3B30]">// Feed</p>
          <h1 className="mt-2 font-display text-5xl uppercase leading-[0.9] tracking-tight text-white md:text-6xl">
            Following
          </h1>
        </div>
        {typeof count === "number" && count > 0 && (
          <span className="font-mono text-[11px] uppercase tracking-widest text-neutral-500" data-testid="feed-count">
            {count} events
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

function EmptyState({ title, body, testid }) {
  return (
    <div data-testid={testid} className="mt-6 border border-dashed border-white/15 py-20 text-center">
      <Rss className="mx-auto h-8 w-8 text-neutral-600" />
      <h2 className="mt-4 font-display text-3xl uppercase text-white">{title}</h2>
      <p className="mx-auto mt-3 max-w-md text-sm text-neutral-400">{body}</p>
      <Link
        to="/"
        className="mt-6 inline-block border border-white/20 bg-[#FF3B30] px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-white hover:bg-white hover:text-black"
        data-testid="feed-empty-cta"
      >
        Back to catalogue
      </Link>
    </div>
  );
}

function FeedItem({ item }) {
  const { user, anime, kind, timestamp } = item;
  const relative = formatRelative(timestamp);
  const rating = anime.user_rating;
  const initial = (user.display_name || user.handle || "?").charAt(0).toUpperCase();
  return (
    <li
      data-testid={`feed-item-${user.handle}-${anime.mal_id}`}
      className="grid grid-cols-[56px_84px_1fr] items-center gap-4 py-4 md:grid-cols-[64px_100px_1fr]"
    >
      <Link to={`/u/${user.handle}`} className="block" title={`@${user.handle}`} data-testid={`feed-avatar-${user.handle}`}>
        {user.picture ? (
          <img src={user.picture} alt={user.display_name} crossOrigin="anonymous"
            className="h-12 w-12 border border-white/15 object-cover md:h-14 md:w-14" />
        ) : (
          <div className="flex h-12 w-12 items-center justify-center border border-white/15 bg-[#111] font-display text-2xl uppercase text-white md:h-14 md:w-14">
            {initial}
          </div>
        )}
      </Link>

      <Link
        to={`/anime/${anime.mal_id}`}
        className="block aspect-[2/3] w-[84px] overflow-hidden border border-white/10 bg-[#111] md:w-[100px]"
        data-testid={`feed-poster-${anime.mal_id}`}
      >
        {anime.image_url ? (
          <img src={anime.image_url} alt={anime.title} loading="lazy" crossOrigin="anonymous"
            className="h-full w-full object-cover" />
        ) : null}
      </Link>

      <div className="min-w-0">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">
          <Link to={`/u/${user.handle}`} className="text-white hover:text-[#FF3B30]" data-testid={`feed-handle-${user.handle}`}>
            @{user.handle}
          </Link>
          <span className="mx-2 text-neutral-700">·</span>
          <span className={kind === "added" ? "text-[#FF3B30]" : "text-emerald-400"}>
            {actionText(kind, anime.status)}
          </span>
          {relative && <><span className="mx-2 text-neutral-700">·</span><span>{relative}</span></>}
        </p>
        <Link to={`/anime/${anime.mal_id}`} className="mt-1 block truncate font-display text-2xl uppercase tracking-tight text-white hover:text-[#FF3B30] md:text-3xl">
          {anime.title}
        </Link>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {rating != null && (
            <span className="inline-flex items-center gap-1 border border-white/15 bg-black/60 px-2 py-0.5 font-mono text-[10px] text-white">
              <Star className="h-3 w-3 fill-[#FF3B30] text-[#FF3B30]" /> {Number(rating).toFixed(1)}
            </span>
          )}
          {(anime.genres || []).map((g) => (
            <span key={g} className="border border-white/10 px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest text-neutral-400">
              {g}
            </span>
          ))}
        </div>
        {anime.notes && (
          <p className="mt-2 line-clamp-2 text-sm italic text-neutral-400">&ldquo;{anime.notes}&rdquo;</p>
        )}
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------

function actionText(kind, status) {
  if (kind === "added") {
    if (status === "completed") return "Completed";
    if (status === "watching") return "Started Watching";
    if (status === "plan_to_watch") return "Added to Plan";
    if (status === "dropped") return "Dropped";
    if (status === "on_hold") return "Put on Hold";
    return "Added";
  }
  if (status === "completed") return "Marked Completed";
  if (status === "watching") return "Now Watching";
  if (status === "dropped") return "Dropped";
  if (status === "on_hold") return "On Hold";
  return "Updated";
}

function formatRelative(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diff = Date.now() - then;
  const sec = Math.round(diff / 1000);
  if (sec < 60) return "just now";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.round(hr / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.round(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.round(months / 12)}y ago`;
}
